import React, { useEffect, useRef } from 'react';
import { useNotifications, Notification } from '../../contexts/NotificationContext';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  Check,
  CheckCheck,
  CheckCircle,
  RotateCcw,
  Trash2,
  Info,
  AlertCircle,
  AlertTriangle,
  Clock,
  X,
} from 'lucide-react';

const NotificationDrawer: React.FC = () => {
  const {
    notifications,
    unreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    removeNotification,
    isDrawerOpen,
    setDrawerOpen,
  } = useNotifications();

  const navigate = useNavigate();
  const drawerRef = useRef<HTMLDivElement>(null);

  // Close drawer on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (drawerRef.current && !drawerRef.current.contains(event.target as Node)) {
        setDrawerOpen(false);
      }
    };

    if (isDrawerOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isDrawerOpen, setDrawerOpen]);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isDrawerOpen]);

  if (!isDrawerOpen) return null;

  const getTypeIcon = (type: Notification['type'], isUnread: boolean) => {
    const iconClass = isUnread ? 'shrink-0' : 'shrink-0 opacity-50';
    switch (type) {
      case 'success':
        return <CheckCircle className={`text-emerald-500 ${iconClass}`} size={16} />;
      case 'warning':
        return <AlertTriangle className={`text-amber-500 ${iconClass}`} size={16} />;
      case 'error':
        return <AlertCircle className={`text-rose-500 ${iconClass}`} size={16} />;
      default:
        return <Info className={`text-[#15BBAE] ${iconClass}`} size={16} />;
    }
  };

  const handleNotificationClick = (notif: Notification) => {
    if (!notif.is_read) {
      markAsRead(notif.id);
    }
    if (notif.link) {
      setDrawerOpen(false);
      navigate(notif.link);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] overflow-hidden" role="dialog" aria-modal="true">
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity"
        onClick={() => setDrawerOpen(false)}
      />
      <div className="fixed inset-y-0 right-0 max-w-sm sm:max-w-md w-full flex">
        <div
          ref={drawerRef}
          className="w-full h-full bg-white shadow-2xl flex flex-col transform transition-transform duration-300 ease-in-out"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-neutral-200 flex justify-between items-center bg-[#F8FCFB]">
            <div className="flex items-center space-x-2.5">
              <div className="w-8 h-8 rounded-lg bg-[#E9F7F5] text-[#15BBAE] flex items-center justify-center">
                <Bell size={18} />
              </div>
              <div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-base font-bold text-[#10253A]">Notifications</h2>
                  {unreadCount > 0 && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#D96F91]/20 text-[#D96F91] font-semibold">
                      {unreadCount}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={() => markAllAsRead()}
                  className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium bg-white border border-neutral-200 text-neutral-700 hover:text-[#15BBAE] hover:border-[#15BBAE] rounded-md transition-all shadow-xs cursor-pointer"
                  title="Mark all as read"
                >
                  <CheckCheck size={14} className="text-[#15BBAE]" />
                  <span>Mark all</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setDrawerOpen(false)}
                className="text-neutral-400 hover:text-neutral-600 p-1 rounded-lg hover:bg-neutral-100 transition-colors"
                aria-label="Close drawer"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-2.5">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-neutral-400 py-12">
                <div className="w-12 h-12 rounded-full bg-neutral-100 flex items-center justify-center mb-2.5 text-neutral-300">
                  <Bell size={24} />
                </div>
                <p className="text-sm font-semibold text-neutral-700">No notifications yet</p>
                <p className="text-xs text-neutral-400 mt-0.5">You are completely caught up!</p>
              </div>
            ) : (
              notifications.map((notif) => {
                const isUnread = !notif.is_read;
                return (
                  <div
                    key={notif.id}
                    className={`relative p-3.5 rounded-xl border transition-all cursor-pointer group ${
                      isUnread
                        ? 'bg-[#F8FCFB] border-[#15BBAE]/30 hover:border-[#15BBAE]/60'
                        : 'bg-white border-neutral-200 hover:border-neutral-300'
                    }`}
                    onClick={() => handleNotificationClick(notif)}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-start space-x-3 flex-1 min-w-0">
                        <div className="mt-0.5">{getTypeIcon(notif.type, isUnread)}</div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2">
                            {isUnread && (
                              <span
                                className="w-2 h-2 rounded-full bg-[#15BBAE] shrink-0"
                                title="Unread notification"
                              />
                            )}
                            <p
                              className={`text-sm truncate ${
                                isUnread
                                  ? 'font-semibold text-neutral-900'
                                  : 'font-normal text-neutral-600'
                              }`}
                            >
                              {notif.title}
                            </p>
                          </div>
                          <p
                            className={`text-xs mt-1 leading-relaxed ${
                              isUnread ? 'text-neutral-700' : 'text-neutral-500'
                            }`}
                          >
                            {notif.message}
                          </p>
                          <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 mt-1.5">
                            <Clock size={11} />
                            <span>
                              {new Date(notif.createdAt).toLocaleString(undefined, {
                                dateStyle: 'medium',
                                timeStyle: 'short',
                              })}
                            </span>
                          </div>
                        </div>
                      </div>

                      {/* Quick actions */}
                      <div className="flex items-center space-x-1 shrink-0">
                        {isUnread ? (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notif.id);
                            }}
                            className="p-1 text-neutral-500 hover:text-[#15BBAE] rounded hover:bg-white"
                            title="Mark as read"
                          >
                            <Check size={14} className="text-[#15BBAE]" />
                          </button>
                        ) : (
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsUnread(notif.id);
                            }}
                            className="p-1 text-neutral-400 hover:text-neutral-700 rounded hover:bg-neutral-100"
                            title="Mark as unread"
                          >
                            <RotateCcw size={13} />
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeNotification(notif.id);
                          }}
                          className="p-1 text-neutral-400 hover:text-rose-600 rounded hover:bg-neutral-100"
                          title="Delete notification"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationDrawer;
